import React, { useCallback, useEffect, useRef, useState } from 'react'
import { View, XStack, XStackProps } from 'tamagui'
import { NavigationControl } from '@components/NavigationControl'
import { Button } from '@tamagui/button'

export interface IHeroCarouselProps {
  numberOfSlides: number
  children: React.ReactNode
  height?: number
  displayControls?: boolean
  indicatorsStyles?: XStackProps
  sliderTitle?: React.ReactNode
  autoPlay?: boolean
  autoPlayInterval?: number
  stopOnHover?: boolean
  isCarousel?: boolean
  maxHeight?: number
}

export function HeroCarousel({
  numberOfSlides,
  children,
  height,
  displayControls = true,
  indicatorsStyles,
  autoPlay = false,
  autoPlayInterval = 3000,
  stopOnHover = true,
  maxHeight,
}: IHeroCarouselProps) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const faderRef = useRef<HTMLDivElement | null>(null)
  const hoveringRef = useRef(false)
  const draggingRef = useRef(false)
  const isPaused = () =>
    draggingRef.current || (stopOnHover && hoveringRef.current)

  const goTo = useCallback(
    (index: number) => {
      if (numberOfSlides <= 0) return
      setCurrentSlide(
        ((index % numberOfSlides) + numberOfSlides) % numberOfSlides,
      )
    },
    [numberOfSlides],
  )

  const handlePrev = useCallback(() => {
    goTo(currentSlide - 1)
  }, [goTo, currentSlide])

  const handleNext = useCallback(() => {
    goTo(currentSlide + 1)
  }, [goTo, currentSlide])

  // Autoplay: advances on an interval, honoring pause (hover/drag).
  useEffect(() => {
    if (!autoPlay || numberOfSlides <= 1) return
    const id = setInterval(() => {
      if (isPaused()) return
      setCurrentSlide((prev) => (prev + 1) % numberOfSlides)
    }, autoPlayInterval)
    return () => clearInterval(id)
    // currentSlide in deps: restart the timer on every navigation so the full
    // interval is counted again after manual/indicator navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, numberOfSlides, autoPlayInterval, stopOnHover, currentSlide])

  // Manual drag (swipe) + hover pause.
  useEffect(() => {
    const el = faderRef.current
    if (!el) return

    let startX = 0

    const onPointerDown = (e: PointerEvent) => {
      draggingRef.current = true
      startX = e.clientX
    }
    const onPointerUp = (e: PointerEvent) => {
      if (!draggingRef.current) return
      draggingRef.current = false
      const dx = e.clientX - startX
      const threshold = 40
      if (dx <= -threshold) {
        setCurrentSlide((prev) => (prev + 1) % numberOfSlides)
      } else if (dx >= threshold) {
        setCurrentSlide((prev) => (prev - 1 + numberOfSlides) % numberOfSlides)
      }
    }
    const onMouseEnter = () => {
      hoveringRef.current = true
    }
    const onMouseLeave = () => {
      hoveringRef.current = false
    }

    el.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('pointerup', onPointerUp)
    el.addEventListener('mouseenter', onMouseEnter)
    el.addEventListener('mouseleave', onMouseLeave)

    return () => {
      el.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('pointerup', onPointerUp)
      el.removeEventListener('mouseenter', onMouseEnter)
      el.removeEventListener('mouseleave', onMouseLeave)
    }
  }, [numberOfSlides])

  return (
    <View>
      <View
        ref={faderRef as never}
        className="fader"
        position="relative"
        width="100%"
        height={height}
        maxHeight={maxHeight}
        justifyContent="center"
        alignSelf="center"
      >
        {numberOfSlides > 1 && displayControls && (
          <>
            <XStack
              position="absolute"
              zIndex={999}
              left={-10}
              top={0}
              bottom={0}
              alignItems="center"
            >
              <NavigationControl
                action={handlePrev}
                chevronDirection="left"
                size={35}
                isCarousel={true}
              />
            </XStack>
            <XStack
              position="absolute"
              zIndex={999}
              right={-10}
              top={0}
              bottom={0}
              alignItems="center"
            >
              <NavigationControl
                action={handleNext}
                chevronDirection="right"
                size={35}
                isCarousel={true}
              />
            </XStack>
          </>
        )}

        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            const isActive = index === currentSlide
            return React.cloneElement(
              child as React.ReactElement<{
                className?: string
                style?: React.CSSProperties
              }>,
              {
                className:
                  `${(child.props as { className?: string }).className || ''} fader__slide`.trim(),
                style: {
                  position: 'absolute',
                  opacity: isActive ? 1 : 0,
                  zIndex: isActive ? 100 : 1,
                  transition: 'opacity 0.7s ease-in-out',
                  pointerEvents: isActive ? 'auto' : 'none',
                },
              },
            )
          }
          return child
        })}
      </View>
      {numberOfSlides > 1 && (
        <XStack
          position="relative"
          justifyContent="center"
          gap={16}
          paddingTop={60}
          {...indicatorsStyles}
        >
          {Array.from({ length: numberOfSlides }).map((_, index) => (
            <Button
              key={index}
              onPress={() => goTo(index)}
              animation="slow"
              bottom={0}
              size={8}
              borderRadius={100}
              width={currentSlide === index ? 32 : 8}
              bg={currentSlide === index ? '$accent' : '$control-inactive'}
              borderColor={
                currentSlide === index ? '$accent' : '$control-inactive'
              }
              hoverStyle={{ borderColor: '$accent', bg: '$accent' }}
              pressStyle={{ borderColor: '$accent', bg: '$accent' }}
              style={{
                transition: 'width 0.3s ease-in-out',
              }}
            />
          ))}
        </XStack>
      )}
    </View>
  )
}
